import { useCallback, useEffect, useMemo, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";

import useToastPromise from "../../../../../../hooks/useToast";
import {
  createBoardIntegration,
  deleteBoardIntegration,
  updateBoardIntegration,
} from "../../../../../../services/apiBoards";
import { getResponseData } from "../../../../../../reactQuery/response";
import { useBoardIntegrations } from "../../../../../../reactQuery/integrations";

export const NOTIFICATION_EVENT_OPTIONS = [
  { value: "notify_assigned", label: "Assigned Ticket" },
  { value: "notify_comment", label: "Comments" },
  { value: "notify_status_change", label: "Status Change" },
  { value: "notify_worklog", label: "Worklog" },
];

export function normalizeIntegrationEvents(integration) {
  return NOTIFICATION_EVENT_OPTIONS.reduce((accumulator, option) => {
    accumulator[option.value] = Boolean(integration?.[option.value]);
    return accumulator;
  }, {});
}

export function normalizeIntegrationItem(item, index) {
  return {
    integration_id:
      item?.integration_id ||
      item?.id ||
      item?.board_integration_id ||
      `temp-${index}`,
    webhook_url:
      item?.webhook_url || item?.webhookUrl || item?.channel_webhook_url || "",
    events: normalizeIntegrationEvents(item),
    provider: item.provider,
  };
}

export function extractBoardIntegrations(response) {
  const responseData = getResponseData(response, []);

  if (Array.isArray(responseData)) {
    return responseData.map(normalizeIntegrationItem);
  }

  if (Array.isArray(responseData?.integrations)) {
    return responseData.integrations.map(normalizeIntegrationItem);
  }

  if (Array.isArray(response?.data?.data?.responseData)) {
    return response.data.data.responseData.map(normalizeIntegrationItem);
  }

  if (Array.isArray(response?.data?.data?.responseData?.integrations)) {
    return response.data.data.responseData.integrations.map(
      normalizeIntegrationItem
    );
  }

  return [];
}

export function buildIntegrationPayload({ webhookUrl, selectedEvents }) {
  return {
    webhook_url: webhookUrl.trim(),
    ...NOTIFICATION_EVENT_OPTIONS.reduce((accumulator, option) => {
      accumulator[option.value] = Boolean(selectedEvents?.[option.value]);
      return accumulator;
    }, {}),
  };
}

export function useSetupBoardNotifications({ open, boardId, currentBoard }) {
  const queryClient = useQueryClient();
  const { showToastPromise } = useToastPromise();
  const resolvedBoardId = boardId || currentBoard?.board_id;
  const boardName = currentBoard?.board_name || "this board";
  const [formState, setFormState] = useState({
    open: false,
    mode: "add",
    integration: null,
  });
  const [deleteState, setDeleteState] = useState({
    open: false,
    integration: null,
  });

  const integrationsQuery = useBoardIntegrations(resolvedBoardId, open);

  const integrations = useMemo(
    () => extractBoardIntegrations(integrationsQuery.integrationsData),
    [integrationsQuery.integrationsData]
  );

  const invalidateBoardIntegrations = useCallback(() => {
    queryClient.invalidateQueries({
      queryKey: ["boardIntegrations", resolvedBoardId],
    });
  }, [queryClient, resolvedBoardId]);

  const closeFormDialog = useCallback(() => {
    setFormState({
      open: false,
      mode: "add",
      integration: null,
    });
  }, []);

  const closeDeleteDialog = useCallback(() => {
    setDeleteState({
      open: false,
      integration: null,
    });
  }, []);

  useEffect(() => {
    if (open) return;
    closeFormDialog();
    closeDeleteDialog();
  }, [closeDeleteDialog, closeFormDialog, open]);

  const createIntegrationMutation = useMutation({
    mutationFn: async (payload) =>
      showToastPromise(
        () => createBoardIntegration(resolvedBoardId, payload),
        payload
      ),
    onSuccess: () => {
      invalidateBoardIntegrations();
      closeFormDialog();
    },
  });

  const updateIntegrationMutation = useMutation({
    mutationFn: async ({ integrationId, payload }) =>
      showToastPromise(
        () => updateBoardIntegration({ integrationId, data: payload }),
        payload
      ),
    onSuccess: () => {
      invalidateBoardIntegrations();
      closeFormDialog();
    },
  });

  const deleteIntegrationMutation = useMutation({
    mutationFn: async (integrationId) =>
      showToastPromise(() => deleteBoardIntegration(integrationId)),
    onSuccess: () => {
      invalidateBoardIntegrations();
      closeDeleteDialog();
    },
  });

  const handleAddIntegration = useCallback(() => {
    setFormState({
      open: true,
      mode: "add",
      integration: null,
    });
  }, []);

  const handleEditIntegration = useCallback((integration) => {
    setFormState({
      open: true,
      mode: "edit",
      integration,
    });
  }, []);

  const handleDeleteIntegration = useCallback((integration) => {
    setDeleteState({
      open: true,
      integration,
    });
  }, []);

  const handleSubmitIntegration = useCallback(
    (values) => {
      if (formState.mode === "edit" && formState.integration?.integration_id) {
        updateIntegrationMutation.mutate({
          integrationId: formState.integration.integration_id,
          payload: buildIntegrationPayload(values),
        });
        return;
      }

      createIntegrationMutation.mutate(buildIntegrationPayload(values));
    },
    [createIntegrationMutation, formState, updateIntegrationMutation]
  );

  const handleConfirmDelete = useCallback(() => {
    if (!deleteState.integration?.integration_id) return;
    deleteIntegrationMutation.mutate(deleteState.integration.integration_id);
  }, [deleteIntegrationMutation, deleteState.integration]);

  return {
    boardName,
    integrations,
    integrationsQuery,
    formState,
    deleteState,
    isSaving:
      createIntegrationMutation.isPending || updateIntegrationMutation.isPending,
    isDeleting: deleteIntegrationMutation.isPending,
    closeDeleteDialog,
    closeFormDialog,
    handleAddIntegration,
    handleConfirmDelete,
    handleDeleteIntegration,
    handleEditIntegration,
    handleSubmitIntegration,
  };
}
